const rootBusyState = new WeakMap();
const BLOCKED_CONTROL_SELECTOR = 'a[href], button, select';
// A control that must stay usable while the surface is busy (it does not change the surface state).
const EXEMPT_CONTROL_SELECTOR = '[data-paged-search-busy-exempt]';

/**
 * A control whose disabled state has its own owner (`data-paged-search-busy-owner`, e.g. a manual list card):
 * the lock neither snapshots nor restores it, and the owner derives it from `data-loading` and
 * `paged-search:busychange` together with its own state.
 */
function hasOwnBusyOwner(control) {
    return String(control.dataset?.pagedSearchBusyOwner ?? '') !== '';
}

export function acquirePagedSearchBusy(root, reason = 'request') {
    if (!(root instanceof HTMLElement)) {
        return () => {};
    }

    const token = Symbol('paged-search-busy');
    const state = rootBusyState.get(root) ?? createBusyState();
    state.tokens.set(token, String(reason || 'request'));
    rootBusyState.set(root, state);
    syncBusyState(root, state, String(reason || 'request'));

    return () => {
        const currentState = rootBusyState.get(root);
        if (!currentState || !currentState.tokens.has(token)) {
            return;
        }

        currentState.tokens.delete(token);
        syncBusyState(root, currentState, String(reason || 'request'));

        if (currentState.tokens.size === 0) {
            rootBusyState.delete(root);
        }
    };
}

export function refreshPagedSearchBusy(root) {
    const state = rootBusyState.get(root);
    if (!(root instanceof HTMLElement) || !state || state.tokens.size === 0) {
        return;
    }

    disableControls(root, state);
}

function createBusyState() {
    return {
        tokens: new Map(),
        disabledControls: new Map(),
        focusTarget: null,
        interactionBlocker: null,
        interactionBlockerRoot: null,
        wasBusy: false,
    };
}

function syncBusyState(root, state, reason) {
    const busy = state.tokens.size > 0;

    root.dataset.loading = busy ? 'true' : 'false';
    root.setAttribute('aria-busy', busy ? 'true' : 'false');

    if (busy) {
        disableControls(root, state);
    } else {
        restoreControls(state);
    }

    if (state.wasBusy === busy) {
        return;
    }

    state.wasBusy = busy;
    root.dispatchEvent(new CustomEvent('paged-search:busychange', {
        bubbles: true,
        detail: {
            busy,
            reason,
        },
    }));
}

function disableControls(root, state) {
    ensureInteractionBlocker(root, state);

    const activeElement = root.ownerDocument?.activeElement ?? globalThis.document?.activeElement ?? null;
    const controls = root.querySelectorAll(BLOCKED_CONTROL_SELECTOR);
    controls.forEach((control) => {
        if (
            !(control instanceof HTMLElement)
            || control.matches(EXEMPT_CONTROL_SELECTOR)
            || hasOwnBusyOwner(control)
        ) {
            return;
        }

        if (!state.disabledControls.has(control)) {
            state.disabledControls.set(control, readControlSnapshot(control));
        }

        control.setAttribute('aria-disabled', 'true');
        control.setAttribute('tabindex', '-1');

        if (
            activeElement === control
            && state.focusTarget === null
            && typeof control.blur === 'function'
        ) {
            state.focusTarget = control;
            control.blur();
        }

        if ('disabled' in control) {
            control.disabled = true;
        }
    });
}

function restoreControls(state) {
    removeInteractionBlocker(state);

    state.disabledControls.forEach((snapshot, control) => {
        if (!(control instanceof HTMLElement)) {
            return;
        }

        if (snapshot.ariaDisabled === null) {
            control.removeAttribute('aria-disabled');
        } else {
            control.setAttribute('aria-disabled', snapshot.ariaDisabled);
        }

        if (snapshot.tabIndex === null) {
            control.removeAttribute('tabindex');
        } else {
            control.setAttribute('tabindex', snapshot.tabIndex);
        }

        if ('disabled' in control) {
            control.disabled = snapshot.disabled;
        }
    });

    state.disabledControls.clear();

    const focusTarget = state.focusTarget;
    state.focusTarget = null;
    const documentRef = focusTarget?.ownerDocument ?? null;
    const activeElement = documentRef?.activeElement ?? null;
    if (
        focusTarget instanceof HTMLElement
        && focusTarget.isConnected
        && (activeElement === null || activeElement === documentRef?.body)
        && typeof focusTarget.focus === 'function'
    ) {
        focusTarget.focus({ preventScroll: true });
    }
}

function readControlSnapshot(control) {
    return {
        ariaDisabled: control.getAttribute('aria-disabled'),
        disabled: 'disabled' in control ? control.disabled === true : false,
        tabIndex: control.getAttribute('tabindex'),
    };
}

function ensureInteractionBlocker(root, state) {
    if (typeof root.addEventListener !== 'function' || state.interactionBlocker !== null) {
        return;
    }

    state.interactionBlocker = (event) => {
        const target = event?.target;
        if (target === null || typeof target !== 'object' || typeof target.closest !== 'function') {
            return;
        }

        const control = target.closest(BLOCKED_CONTROL_SELECTOR);
        if (!(control instanceof HTMLElement) || !state.disabledControls.has(control)) {
            return;
        }

        event.preventDefault?.();
        event.stopPropagation?.();
        event.stopImmediatePropagation?.();
    };

    root.addEventListener('click', state.interactionBlocker, true);
    state.interactionBlockerRoot = root;
}

function removeInteractionBlocker(state) {
    const blocker = state.interactionBlocker;
    const root = state.interactionBlockerRoot;
    if (blocker === null || !(root instanceof HTMLElement)) {
        return;
    }

    root.removeEventListener?.('click', blocker, true);
    state.interactionBlocker = null;
    state.interactionBlockerRoot = null;
}
